import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Tooltip from "@/v2-ui/tooltip"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import CustomerInvoiceStatusBadge from "@/v2-backoffice-client/customer/billing/CustomerInvoiceStatusBadge"
import { FENERUM_INVOICE_BASE_URL } from "@/v2-backoffice-client/constants"
import type { CustomerInvoiceFragment } from "@/v2-backoffice-client/customer/billing/__types__/customer.invoice.fragment"
import {
  formatInvoiceAmount,
  getInvoiceStatusDisplay,
} from "@/v2-backoffice-client/customer/billing/customerBilling.utils"

function createCustomerInvoiceColumns() {
  return createColumns<CustomerInvoiceFragment>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      InvoiceNumber: {
        id: "InvoiceNumber",
        label: "Invoice",
        description: "Opens the invoice in Fenerum.",
        defaultSize: 90,
        getValue: (row) =>
          row.InvoiceNumber != null
            ? String(row.InvoiceNumber)
            : (row.ExternalInvoiceNumber ?? ""),
        renderCell({ row }) {
          const { InvoiceNumber, ExternalInvoiceNumber } = row.original
          return InvoiceNumber != null
            ? `#${InvoiceNumber}`
            : (ExternalInvoiceNumber ?? null)
        },
        // Straight to the invoice's Fenerum page — the mirror is read-only,
        // so anything beyond reading (credit, resend, dunning) happens there.
        to: ({ row }) =>
          row.original.ExternalInvoiceUuid
            ? `${FENERUM_INVOICE_BASE_URL}/${row.original.ExternalInvoiceUuid}/`
            : undefined,
      },
      InvoiceStatus: {
        id: "InvoiceStatus",
        label: "Status",
        description:
          "Fenerum's own status: Open = booked and not yet due, Overdue = past the due date and unpaid, Non-payment = written off or in collection. Hover a status for what it means.",
        defaultSize: 110,
        enableGrouping: true,
        getValue: (row) => getInvoiceStatusDisplay(row.InvoiceStatus).label,
        renderCell({ row }) {
          return (
            <CustomerInvoiceStatusBadge status={row.original.InvoiceStatus} />
          )
        },
        exportValue({ row }) {
          return getInvoiceStatusDisplay(row.original.InvoiceStatus).label
        },
      },
      PurchasedLines: {
        id: "PurchasedLines",
        label: "Purchased",
        description:
          "What the invoice actually bought, straight off its lines — hover for finance's per-line notes and the service period the money covers.",
        defaultSize: 200,
        enableGrouping: true,
        getValue: (row) => row.PurchasedLines ?? "",
        renderCell({ row }) {
          const { PurchasedLines, LineSupplements, PeriodFrom, PeriodTo } =
            row.original
          if (!PurchasedLines) return null
          if (!LineSupplements && !PeriodFrom) return PurchasedLines
          return (
            <Tooltip>
              <Tooltip.Trigger asChild>
                <span className="cursor-help">{PurchasedLines}</span>
              </Tooltip.Trigger>
              <Tooltip.Content side="top" className="ui-tooltip-content--panel">
                <div className="flex max-w-xs flex-col gap-1 p-3 text-xs">
                  <p className="font-medium">{PurchasedLines}</p>
                  {PeriodFrom && (
                    <p className="text-muted-foreground">
                      Period: {formatDateString(PeriodFrom)} –{" "}
                      {formatDateString(PeriodTo)}
                    </p>
                  )}
                  {LineSupplements && (
                    <p className="text-muted-foreground">{LineSupplements}</p>
                  )}
                </div>
              </Tooltip.Content>
            </Tooltip>
          )
        },
      },
      Period: {
        id: "Period",
        label: "Period",
        description: "The service period the invoice covers (from its lines).",
        defaultSize: 150,
        defaultHidden: true,
        getValue: (row) =>
          row.PeriodFrom ? formatDateString(row.PeriodFrom) : "",
        renderCell({ row }) {
          const { PeriodFrom, PeriodTo } = row.original
          if (!PeriodFrom) return null
          return `${formatDateString(PeriodFrom)} – ${formatDateString(PeriodTo)}`
        },
        exportValue({ row }) {
          const { PeriodFrom, PeriodTo } = row.original
          if (!PeriodFrom) return ""
          return `${formatDateString(PeriodFrom)} – ${formatDateString(PeriodTo)}`
        },
      },
      LineSupplements: {
        id: "LineSupplements",
        label: "Line Notes",
        description:
          "Finance's free-text notes on the invoice lines — often the only written record of a group-billing arrangement.",
        defaultSize: 220,
        defaultHidden: true,
        getValue: (row) => row.LineSupplements ?? "",
      },
      InvoiceDate: {
        id: "InvoiceDate",
        label: "Date",
        defaultSize: 100,
        renderCell({ row }) {
          const value = row.original.InvoiceDate
          return value ? formatDateString(value) : null
        },
      },
      DueDate: {
        id: "DueDate",
        label: "Due",
        defaultSize: 100,
        renderCell({ row }) {
          const value = row.original.DueDate
          return value ? formatDateString(value) : null
        },
      },
      Total: {
        id: "Total",
        label: "Total",
        description: "Invoice total including VAT.",
        defaultSize: 110,
        renderCell({ row }) {
          const { Total, Currency } = row.original
          return formatInvoiceAmount(Total, Currency)
        },
      },
      Balance: {
        id: "Balance",
        label: "Outstanding",
        description: "What is still unpaid — 0 once settled.",
        defaultSize: 110,
        renderCell({ row }) {
          const { Balance, Currency } = row.original
          if (Balance == null || Balance === 0) return null
          return formatInvoiceAmount(Balance, Currency)
        },
      },
      Subtotal: {
        id: "Subtotal",
        label: "Subtotal",
        defaultSize: 110,
        defaultHidden: true,
        renderCell({ row }) {
          const { Subtotal, Currency } = row.original
          return formatInvoiceAmount(Subtotal, Currency)
        },
      },
      Vat: {
        id: "Vat",
        label: "VAT",
        defaultSize: 100,
        defaultHidden: true,
        renderCell({ row }) {
          const { Vat, Currency } = row.original
          return formatInvoiceAmount(Vat, Currency)
        },
      },
      CollectionMethod: {
        id: "CollectionMethod",
        label: "Collection",
        defaultSize: 90,
        defaultHidden: true,
        enableGrouping: true,
      },
      SyncedAt: {
        id: "SyncedAt",
        label: "Synced",
        defaultSize: 90,
        defaultHidden: true,
        renderCell({ row }) {
          const value = row.original.SyncedAt
          return value ? formatDateString(value) : null
        },
      },
      ExternalInvoiceUuid: {
        id: "ExternalInvoiceUuid",
        label: "Fenerum Uuid",
        defaultSize: 250,
        defaultHidden: true,
        to: ({ value }) => {
          if (!value) return
          return `${FENERUM_INVOICE_BASE_URL}/${value}/`
        },
      },
      Id: { id: "Id", label: "Id", defaultSize: 50, defaultHidden: true },
    },
  })
}

export type CustomerBillingInvoiceTableProps = Omit<
  TableProps<CustomerInvoiceFragment>,
  "data" | "columns"
> & {
  invoices: CustomerInvoiceFragment[]
  isLoading?: boolean
  error?: Error
  onRefresh?(): void
}

// The customer's invoice history (lic.Invoices via its account row), newest
// first from the proc. Presentational only — the Billing tab owns the query.
function CustomerBillingInvoiceTable(props: CustomerBillingInvoiceTableProps) {
  const { className, invoices, isLoading, error, onRefresh, ...tableProps } =
    props

  const columns = useMemo(() => createCustomerInvoiceColumns(), [])

  return (
    <Table
      className={cn("customer-billing-invoice-table", className)}
      tableId="customer-billing-invoice-table"
      data={invoices}
      columns={columns}
      {...tableProps}
    >
      <Table.Toolbar>
        <div className="flex-1" />
        <Table.ToolbarMenu
          onRefresh={onRefresh}
          exportFileName="customer-invoices"
        />
      </Table.Toolbar>

      <Table.Content isLoading={isLoading} error={error} />
    </Table>
  )
}

export default CustomerBillingInvoiceTable
